import dedent from 'ts-dedent';
import { ColumnDataType, Provider, WorkflowComponentParamType } from '../types';
import { SingleOutputWorkflowComponent } from './common/singleoutputcomponent';
const predicates = new Map([
    ['Where Target Intersects Universe', 'ST_INTERSECTS'],
    ['Where Target Contains Universe', 'ST_CONTAINS'],
    ['Where Target Is Within Universe', 'ST_WITHIN'],
    ['Where Target Touches Universe', 'ST_TOUCHES'],
    ['Bounding Rectangle Overlaps', '']
]);
class SpatialMatch extends SingleOutputWorkflowComponent {
    constructor() {
        super({
            name: 'alteryx.spatialmatch',
            group: 'Joins',
            title: 'Spatial Match',
            description: `
                      This component establishes the spatial relationship (contains, intersects, touches, etc.)
                      between two sets of spatial objects.
                      `,
            tags: ['alteryx']
        }, [
            {
                name: 'targets',
                type: WorkflowComponentParamType.InputTable,
                title: 'Targets'
            },
            {
                name: 'universe',
                type: WorkflowComponentParamType.InputTable,
                title: 'Universe'
            },
            {
                name: 'targetscolumn',
                type: WorkflowComponentParamType.Column,
                title: 'Target Spatial Object Field',
                parent: 'targets',
                dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
            },
            {
                name: 'universecolumn',
                type: WorkflowComponentParamType.Column,
                title: 'Universe Spatial Object Field',
                parent: 'universe',
                dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
            },
            {
                name: 'predicate',
                type: WorkflowComponentParamType.Selection,
                title: 'Type of Spatial Match',
                default: predicates.keys().next().value,
                optional: true,
                options: Array.from(predicates.keys())
            },
            {
                name: 'outputintersection',
                type: WorkflowComponentParamType.Boolean,
                title: 'Output Intersection Object (Intersects Only)',
                default: false
            }
        ]);
        this.supportedProviders = [Provider.BigQuery];
    }
    async getCode({ workflow, params, connection, envVars, codeType, schemas }) {
        if (connection.type !== Provider.BigQuery) {
            return null;
        }
        const outputTablename = connection.getOutputTableName(workflow.id, this.metadata.name, params, 'result', codeType);
        const preparedParams = this._prepareParameters(params);
        let select;
        if (preparedParams.predicate === 'Bounding Rectangle Overlaps') {
            select = `SELECT _main.*, %s
                FROM
                  \`${preparedParams.targets}\` _main,
                  \`${preparedParams.universe}\` _joined
                WHERE
                  ST_INTERSECTS(
                    \`${envVars.analyticsToolboxDataset}\`.ST_ENVELOPE(
                      [_main.${preparedParams.targetscolumn}]
                    ),
                    \`${envVars.analyticsToolboxDataset}\`.ST_ENVELOPE(
                      [_joined.${preparedParams.universecolumn}]
                    )
                  )`;
        }
        else {
            select = `SELECT _main.*, %s
                ${preparedParams.outputintersection &&
                preparedParams.predicate === predicates.keys().next().value
                ? `, ST_INTERSECTION(_main.${preparedParams.targetscolumn},
                          _joined.${preparedParams.universecolumn}) AS Intersection `
                : ''}
                FROM
                  \`${preparedParams.targets}\` _main,
                  \`${preparedParams.universe}\` _joined
                WHERE
                  ${predicates.get(preparedParams.predicate)}(
                    _main.${preparedParams.targetscolumn},
                    _joined.${preparedParams.universecolumn}
                  )`;
        }
        const createTable = connection.getCreateTempTableQuery(outputTablename, select, codeType, null);
        const tokens = (preparedParams.universe ?? '').split('.');
        const datasetname = `${tokens[0]}.${tokens[1]}`;
        const tablename = tokens[2];
        const query = dedent `DECLARE alias STRING;
              EXECUTE IMMEDIATE
              '''
                with __alias AS(
                  SELECT CONCAT(
                    '_joined.', column_name, ' AS ', column_name, '_joined'
                  ) col_alias
                  FROM \`${datasetname}\`.INFORMATION_SCHEMA.COLUMNS
                WHERE table_name = '${tablename}'
                )
                SELECT STRING_AGG(col_alias, ', ')
                FROM __alias
              '''
              INTO alias;
              ${connection.executeDynamicSQL({
            sql: createTable,
            addQuotes: true,
            replacements: [["'%s'", 'alias']]
        })}`;
        return {
            query,
            outputs: { result: outputTablename }
        };
    }
}
export { SpatialMatch };
